import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut, Package } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-medical-primary rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-semibold text-xl text-gray-900">MEDICAL SUPPLY</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-medical-primary transition-colors" data-testid="nav-home">Home</Link>
            <Link to="/shop" className="text-gray-700 hover:text-medical-primary transition-colors" data-testid="nav-shop">Shop</Link>
            <Link to="/about" className="text-gray-700 hover:text-medical-primary transition-colors" data-testid="nav-about">About</Link>
            <Link to="/testimonials" className="text-gray-700 hover:text-medical-primary transition-colors" data-testid="nav-testimonials">Testimonials</Link>
            <Link to="/faq" className="text-gray-700 hover:text-medical-primary transition-colors" data-testid="nav-faq">FAQ</Link>
            <Link to="/contact" className="text-gray-700 hover:text-medical-primary transition-colors" data-testid="nav-contact">Contact</Link>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <Link to="/cart" className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="cart-button">
              <ShoppingCart className="w-5 h-5 text-gray-700" />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-medical-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {getCartCount()}
                </span>
              )}
            </Link>

            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-700">Hi, {user.name}</span>
                <Link to="/orders" data-testid="orders-link">
                  <Button variant="outline" size="sm">Orders</Button>
                </Link>
                {user.is_admin && (
                  <Link to="/admin" data-testid="admin-link">
                    <Button variant="outline" size="sm">Admin</Button>
                  </Link>
                )}
                <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="logout-button">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login" data-testid="login-link">
                  <Button variant="outline" size="sm">Login</Button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button className="bg-medical-primary hover:bg-medical-primary/90" size="sm">Register</Button>
                </Link>
              </div>
            )}
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            <Link to="/" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Home</Link>
            <Link to="/shop" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Shop</Link>
            <Link to="/about" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>About</Link>
            <Link to="/testimonials" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Testimonials</Link>
            <Link to="/faq" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>FAQ</Link>
            <Link to="/contact" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Contact</Link>
            <Link to="/cart" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>
              Cart ({getCartCount()})
            </Link>
            {user ? (
              <>
                <Link to="/orders" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Orders</Link>
                {user.is_admin && (
                  <Link to="/admin" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Admin</Link>
                )}
                <button onClick={() => { handleLogout(); setIsOpen(false); }} className="block py-2 text-gray-700 w-full text-left">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Login</Link>
                <Link to="/register" className="block py-2 text-gray-700" onClick={() => setIsOpen(false)}>Register</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;